import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDemoPermissions } from '../hooks/useDemoPermissions';
import { addHousehold as addNewClient } from '../services/householdService';
import { searchClients as searchSuppliers } from '../services/clientService';
import { searchAddressSuggestions } from '../services/addressService';
import { useNavigate, Link } from 'react-router-dom';
import { debounce } from 'lodash';
import { terminology } from '../config/terminology';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import { InputField, TextareaField } from '../components/fields';

const AddAppClientPage = () => {
  const { register, handleSubmit, control, watch, setValue, formState: { errors, isSubmitting } } = useForm({
    mode: 'onBlur',
    defaultValues: {
        clientId: '',
        clientNameSearch: '',
        firstName: '',
        lastName: '',
        addressLine1: '',
        addressLine2: '',
        state: '',
        postalCode: '',
        email: '',
        phoneNumber: '',
        country: 'Australia', // Add default value for country
        adminNotes: '',
        siteContact: {
          name: '',
          phone: '',
          email: ''
        },
        additionalContacts: []
    }
  });
  const navigate = useNavigate();
  const { isDemo } = useDemoPermissions();
  useEffect(() => { if (isDemo) navigate('/households'); }, [isDemo, navigate]);
  const [apiError, setApiError] = useState(null);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [isSearchingSuppliers, setIsSearchingSuppliers] = useState(false);
  const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);

  // Address autocomplete state
  const [addressSearchTerm, setAddressSearchTerm] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddresses, setIsSearchingAddresses] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [lastSelectedAddress, setLastSelectedAddress] = useState(''); // Track last selected address

  const emailValue = watch('email');
  const phoneValue = watch('phoneNumber');
  const supplierNameSearchValue = watch('clientNameSearch');
  const addressLine1Value = watch('addressLine1');

  // Set up field array for additional contacts
  const { fields: additionalContactFields, append: appendAdditionalContact, remove: removeAdditionalContact } = useFieldArray({
    control,
    name: 'additionalContacts'
  });

  const fetchSupplierSuggestions = async (searchTerm) => {
    if (searchTerm.length < 3) {
      setSupplierSuggestions([]);
      setShowSupplierSuggestions(false);
      return;
    }
    setIsSearchingSuppliers(true);
    try {
      const data = await searchSuppliers({ searchString: searchTerm, pageSize: 5 });
      setSupplierSuggestions(data.clients || []);
      setShowSupplierSuggestions(true);
    } catch (error) {
      console.error(`${terminology.client.singular} search error:`, error);
      setSupplierSuggestions([]);
      setShowSupplierSuggestions(false);
    } finally {
      setIsSearchingSuppliers(false);
    }
  };

  const debouncedFetchSuppliers = useCallback(debounce(fetchSupplierSuggestions, 300), []);

  // Address search function
  const fetchAddressSuggestions = async (searchTerm) => {
    console.log('fetchAddressSuggestions called with:', searchTerm);
    if (searchTerm.length < 3) {
      console.log('Search term too short, clearing suggestions');
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    console.log('Starting address search...');
    setIsSearchingAddresses(true);
    try {
      const suggestions = await searchAddressSuggestions(searchTerm);
      console.log('Address search results:', suggestions);
      setAddressSuggestions(suggestions.slice(0, 8)); // Limit to 8 suggestions
      setShowAddressSuggestions(true);
      console.log('Showing address suggestions');
    } catch (error) {
      console.error('Address search error:', error);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    } finally {
      setIsSearchingAddresses(false);
    }
  };

  const debouncedFetchAddresses = useCallback(debounce(fetchAddressSuggestions, 300), []);

  useEffect(() => {
    debouncedFetchSuppliers(supplierSearchTerm);
    return () => debouncedFetchSuppliers.cancel();
  }, [supplierSearchTerm, debouncedFetchSuppliers]);

  // Address search effect
  useEffect(() => {
    console.log('Address search effect triggered:', {
      addressSearchTerm,
      addressLine1Value,
      lastSelectedAddress,
      shouldTrigger: addressSearchTerm.length >= 3 && 
        addressSearchTerm !== lastSelectedAddress
    });
    
    // Only trigger search if:
    // 1. Search term is 3+ characters
    // 2. Search term is different from the last selected address (user is typing something new)
    if (addressSearchTerm.length >= 3 && 
        addressSearchTerm !== lastSelectedAddress) {
      console.log('Triggering debounced address search');
      debouncedFetchAddresses(addressSearchTerm);
    } else {
      console.log('Skipping address search due to conditions');
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
    return () => debouncedFetchAddresses.cancel();
  }, [addressSearchTerm, debouncedFetchAddresses, lastSelectedAddress]);

  const handleSupplierSearchChange = (e) => {
    const value = e.target.value;
    setSupplierSearchTerm(value);
    setValue('clientNameSearch', value);
    setValue('clientId', '');
    if (value.length < 3) {
        setShowSupplierSuggestions(false);
    }
  };

  const handleSelectSupplier = (supplier) => {
    setValue('clientId', supplier.id);
    setValue('clientNameSearch', supplier.clientName);
    setSupplierSearchTerm(supplier.clientName);

    const contactName = `${supplier.caseManagerFirstName || ''} ${supplier.caseManagerLastName || ''}`.trim();
    setValue('siteContact.name', contactName);
    setValue('siteContact.email', supplier.caseManagerEmail || '');
    setValue('siteContact.phone', supplier.phoneNumber || '');

    setSupplierSuggestions([]);
    setShowSupplierSuggestions(false);
  };

  // Address handlers
  const handleAddressSearchChange = (e) => {
    const value = e.target.value;
    console.log('handleAddressSearchChange called with:', value);
    
    setAddressSearchTerm(value);
    setValue('addressLine1', value);
    
    if (value.length < 3) {
      console.log('Value too short, hiding suggestions');
      setShowAddressSuggestions(false);
      return;
    }
    
    // Only clear last selected address if user is typing something completely different
    // (not just a minor variation or continuation of the selected address)
    if (lastSelectedAddress && !value.startsWith(lastSelectedAddress.substring(0, Math.min(5, lastSelectedAddress.length)))) {
      console.log('User typing something new, clearing last selected address');
      setLastSelectedAddress('');
    }
  };

  const handleSelectAddress = (address) => {
    console.log('=== Address selection started ===');
    
    const { householdFormat } = address;
    console.log('Selected address:', address);
    console.log('HouseholdFormat:', householdFormat);
    
    const selectedAddressLine1 = householdFormat.addressLine1 || '';
    console.log('Setting form values...');
    
    setValue('addressLine1', selectedAddressLine1);
    setValue('addressLine2', householdFormat.addressLine2 || '');
    setValue('state', householdFormat.state || address.state || '');
    setValue('postalCode', householdFormat.postalCode || address.postcode || '');
    
    console.log('Updating search term and hiding dropdown...');
    setAddressSearchTerm(selectedAddressLine1);
    setLastSelectedAddress(selectedAddressLine1); // Remember this selection
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    
    console.log('=== Address selection completed ===');
  };

  const onSubmit = async (data) => {
    setApiError(null);
    if (!data.clientId) {
        setApiError(`Please select a valid ${terminology.client.singular.toLowerCase()}.`);
        // Scroll to top to show error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }
    const submitData = { ...data };
    delete submitData.clientNameSearch;

    try {
      await addNewClient(submitData);
      navigate('/households', { state: { message: `${terminology.household.singular} added successfully!` } });
    } catch (error) {
      setApiError(error.message || `Failed to add ${terminology.household.singular.toLowerCase()}. Please try again.`);
      console.error("Submission error:", error);
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const validateEmailOrPhone = () => {
    return !!emailValue || !!phoneValue || 'Either Email or Phone Number must be provided.';
  };

  return (
    <div className="container mx-auto px-4 pt-8 pb-20 max-w-3xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">Add New {terminology.household.singular}</h1>
        <Link to="/households" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" className="bg-white p-8 rounded-3xl shadow-md space-y-6">

        {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}

        {/* Supplier Details Section */}
        <div className="border border-gray-300 rounded-lg p-6 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Supplier Details</h3>
          
          {/* Supplier Name */}
          <div className="relative mb-6">
            <label htmlFor="clientNameSearch" className="block text-sm font-medium text-gray-700">Supplier Name * (Assign this Client to a {terminology.client.singular})</label>
            <input
              id="clientNameSearch"
              type="text"
              {...register("clientNameSearch", { required: `${terminology.client.singular} Name search is required.` })}
              value={supplierNameSearchValue}
              onChange={handleSupplierSearchChange}
              onBlur={() => setTimeout(() => setShowSupplierSuggestions(false), 150)}
              onFocus={() => supplierSuggestions.length > 0 && setShowSupplierSuggestions(true)}
              autoComplete="off"
              className={`mt-1 block w-full px-3 py-2.5 px-4 border ${errors.clientNameSearch || (errors.clientId && !watch('clientId')) ? 'border-red-500' : 'border-gray-300'} rounded-2xl shadow-sm focus:outline-none focus:ring-green-600 focus:border-green-600 sm:text-sm bg-white`}
            />
            <input type="hidden" {...register("clientId", { required: `Please select a ${terminology.client.singular.toLowerCase()} from the search results.` })} />

            {errors.clientNameSearch && <p className="mt-1 text-xs text-red-600">{errors.clientNameSearch.message}</p>}
            {errors.clientId && !watch('clientId') && <p className="mt-1 text-xs text-red-600">{errors.clientId.message}</p>}

            {showSupplierSuggestions && (
              <ul className="absolute z-10 w-full bg-white rounded-2xl border-0 shadow-sm ring-1 ring-inset ring-gray-300 mt-1 max-h-60 overflow-y-auto shadow-lg">
                {isSearchingSuppliers ? (
                  <li className="px-3 py-2 text-gray-500">Searching...</li>
                ) : supplierSuggestions.length > 0 ? (
                  supplierSuggestions.map((supplier) => (
                    <li
                      key={supplier.id}
                      className="px-3 py-2 hover:bg-green-600-light cursor-pointer"
                      onMouseDown={() => handleSelectSupplier(supplier)}
                    >
                      {supplier.clientName}
                    </li>
                  ))
                ) : (
                   supplierSearchTerm.length >= 3 && <li className="px-3 py-2 text-gray-500">No {terminology.client.plural.toLowerCase()} found.</li>
                )}
              </ul>
            )}
          </div>

          {/* Case Manager Contact Details */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Case Manager Contact Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="siteContact.name" className="block text-xs font-medium text-gray-600 mb-1">Name:</label>
                <input
                  id="siteContact.name"
                  type="text"
                  {...register("siteContact.name")}
                  className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 bg-white"
                />
              </div>
              <div>
                <label htmlFor="siteContact.phone" className="block text-xs font-medium text-gray-600 mb-1">Phone:</label>
                <input
                  id="siteContact.phone"
                  type="tel"
                  {...register("siteContact.phone")}
                  className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 bg-white"
                />
              </div>
              <div>
                <label htmlFor="siteContact.email" className="block text-xs font-medium text-gray-600 mb-1">Email:</label>
                <input
                  id="siteContact.email"
                  type="email"
                  {...register("siteContact.email", {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address."
                    }
                  })}
                  className={`mt-1 block w-full px-3 py-2.5 px-4 border ${errors.siteContact?.email ? 'border-red-500' : 'border-gray-300'} rounded-2xl shadow-sm focus:outline-none focus:ring-green-600 focus:border-green-600 sm:text-sm bg-white`}
                />
                {errors.siteContact?.email && <p className="mt-1 text-xs text-red-600">{errors.siteContact.email.message}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Main Site Contact section moved here */}
        <div className="border-b pt-2 mt-6 border-gray-200"> 
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Supplier Contact</h4>
            <div className="bg-gray-50 p-4 rounded-2xl">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="siteContact.name" className="block text-xs font-medium text-gray-600 mb-1">Name:</label>
                  <input
                    id="siteContact.name"
                    type="text"
                    {...register("siteContact.name")}
                    className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                  />
                </div>
                <div>
                  <label htmlFor="siteContact.phone" className="block text-xs font-medium text-gray-600 mb-1">Phone:</label>
                  <input
                    id="siteContact.phone"
                    type="tel"
                    {...register("siteContact.phone")}
                    className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                  />
                </div>
                <div>
                  <label htmlFor="siteContact.email" className="block text-xs font-medium text-gray-600 mb-1">Email:</label>
                  <input
                    id="siteContact.email"
                    type="email"
                    {...register("siteContact.email", {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address."
                      }
                    })}
                    className={`mt-1 block w-full px-3 py-2.5 px-4 border ${errors.siteContact?.email ? 'border-red-500' : 'border-gray-300'} rounded-2xl shadow-sm focus:outline-none focus:ring-green-600 focus:border-green-600 sm:text-sm`}
                  />
                  {errors.siteContact?.email && <p className="mt-1 text-xs text-red-600">{errors.siteContact.email.message}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>

        <input type="hidden" {...register("contactValidation", { validate: validateEmailOrPhone })} />
        {errors.contactValidation && <p className="mb-3 text-xs text-red-600">{errors.contactValidation.message}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <InputField label="Client First Name *" id="firstName"
                    register={register("firstName", { required: "First Name is required." })}
                    error={errors.firstName}
                />
            </div>
            <div>
                <InputField label="Client Last Name *" id="lastName"
                    register={register("lastName", { required: "Last Name is required." })}
                    error={errors.lastName}
                />
            </div>
            <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                <input
                    id="email"
                    type="email"
                    {...register("email", {
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address."
                        }
                    })}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.contactValidation || errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
                 {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>
            <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Phone Number</label>
                <input
                    id="phoneNumber"
                    type="tel"
                    {...register("phoneNumber")}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.contactValidation ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
                />
            </div>
        </div>

        <div className="relative">
          <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700">Address Line 1 *</label>
          <input
            id="addressLine1"
            name="address-line-1-custom"
            type="text"
            {...register("addressLine1", { required: "Address Line 1 is required." })}
            value={addressLine1Value}
            onChange={handleAddressSearchChange}
            onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 150)}
            onFocus={() => addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
            autoComplete="new-password"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            className={`mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.addressLine1 ? "ring-red-500 bg-red-50" : "ring-gray-300"} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6`}
            placeholder="Start typing an address..."
          />
          {errors.addressLine1 && <p className="mt-1 text-xs text-red-600">{errors.addressLine1.message}</p>}

          {showAddressSuggestions && (
            <ul className="absolute z-20 w-full bg-white rounded-2xl border-0 shadow-sm ring-1 ring-inset ring-gray-300 mt-1 max-h-48 overflow-y-auto shadow-lg">
              {isSearchingAddresses ? (
                <li className="px-3 py-2 text-gray-500">Searching addresses...</li>
              ) : addressSuggestions.length > 0 ? (
                addressSuggestions.map((address, index) => (
                  <li
                    key={`${address.placeId}-${index}`}
                    className="px-3 py-2 hover:bg-green-600-light cursor-pointer text-sm break-words"
                    onMouseDown={() => handleSelectAddress(address)}
                  >
                    {address.description}
                  </li>
                ))
              ) : (
                addressSearchTerm.length >= 3 && <li className="px-3 py-2 text-gray-500">No addresses found.</li>
              )}
            </ul>
          )}
        </div>

        <div>
            <InputField label="Address Line 2" id="addressLine2"
                register={register("addressLine2")}
            />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <InputField label="State *" id="state"
                    register={register("state", { required: "State is required." })}
                    error={errors.state}
                />
            </div>
            <div>
                <InputField label="Postal Code *" id="postalCode"
                    register={register("postalCode", { required: "Postal Code is required." })}
                    error={errors.postalCode}
                />
            </div>
            {/* Add Country Field */}
            <div>
                <InputField label="Country" id="country"
                    register={register("country")}
                    readOnly disabled value="Australia"
                />
            </div>
        </div>


        {/* Additional Site Contacts */}
        <div className="border-t pt-6 mt-6 border-gray-200">
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Additional Site Contacts</h4>
                    <button
                        type="button"
                        onClick={() => appendAdditionalContact({ name: '', phone: '', email: '' })}
                        className="bg-green-600 hover:bg-green-600-dark text-white p-1 rounded transition duration-300 flex items-center justify-center"
                        title="Add Contact"
                    >
                        <FaPlus className="text-xs" />
                    </button>
                </div>
                {additionalContactFields.length > 0 ? (
                    <div className="space-y-3">
                        {additionalContactFields.map((field, index) => (
                            <div key={field.id} className="bg-gray-50 p-4 rounded-2xl">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label htmlFor={`additionalContacts.${index}.name`} className="block text-xs font-medium text-gray-600 mb-1">Name:</label>
                                        <input
                                            id={`additionalContacts.${index}.name`}
                                            type="text"
                                            {...register(`additionalContacts.${index}.name`)}
                                            className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor={`additionalContacts.${index}.phone`} className="block text-xs font-medium text-gray-600 mb-1">Phone:</label>
                                        <input
                                            id={`additionalContacts.${index}.phone`}
                                            type="tel"
                                            {...register(`additionalContacts.${index}.phone`)}
                                            className="mt-1 block w-full rounded-2xl border-0 py-2.5 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="flex-1">
                                            <label htmlFor={`additionalContacts.${index}.email`} className="block text-xs font-medium text-gray-600 mb-1">Email:</label>
                                            <input
                                                id={`additionalContacts.${index}.email`}
                                                type="email"
                                                {...register(`additionalContacts.${index}.email`, {
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: "Invalid email address."
                                                    }
                                                })}
                                                className={`mt-1 block w-full px-3 py-2.5 px-4 border ${errors.additionalContacts?.[index]?.email ? 'border-red-500' : 'border-gray-300'} rounded-2xl shadow-sm focus:outline-none focus:ring-green-600 focus:border-green-600 sm:text-sm`}
                                            />
                                            {errors.additionalContacts?.[index]?.email && <p className="mt-1 text-xs text-red-600">{errors.additionalContacts[index].email.message}</p>}
                                        </div>
                                        <div className="flex items-end">
                                            <button
                                                type="button"
                                                onClick={() => removeAdditionalContact(index)}
                                                className="bg-red-500 hover:bg-red-600 text-white p-1 rounded transition duration-300 mb-1 flex items-center justify-center"
                                                title="Remove Contact"
                                            >
                                                <FaTrashAlt className="text-xs" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 italic">No additional site contacts. Click "Add Contact" to add one.</p>
                )}
            </div>
        </div>

        {/* Admin Notes */}
        <div className="border-t pt-6 mt-6 border-gray-200">
            <div>
                <TextareaField label="Admin Notes" id="adminNotes" rows={4}
                    register={register("adminNotes")}
                    placeholder="Add any internal notes about this household..."
                    hint="Internal notes for administrative use only."
                />
            </div>
        </div>

         <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              <div className="container mx-auto px-6 py-3 max-w-3xl flex justify-end gap-3">
          <Link to="/households" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded transition duration-300">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || isSearchingSuppliers}
            className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-2xl shadow-sm focus:outline-none focus:shadow-outline transition duration-300 disabled:opacity-50"
          >
            {isSubmitting ? 'Adding...' : `Add ${terminology.household.singular}`}
          </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddAppClientPage;
